import { supabase } from '../lib/supabase.js';
import './etoll-weekly-ui.js';

const listeners = new Set();
const activeJobs = new Map();

const POLL_INTERVAL_MS = 2500;
const WATCH_TIMEOUT_MS = 15 * 60 * 1000;
const SERVER_CALCULATORS = new Set(['etoll-pl']);

function emit(message) {
  listeners.forEach(listener => {
    try {
      listener(message);
    } catch (error) {
      console.error('[D24 SERVER CALCULATOR] Błąd listenera:', error);
    }
  });
}

function sleep(ms) {
  return new Promise(resolve => window.setTimeout(resolve, ms));
}

function normalizeError(error, fallback) {
  return error?.message || error?.details || error?.hint || fallback;
}

async function requireSession() {
  if (!supabase) throw new Error('Brak połączenia z Supabase.');

  const { data, error } = await supabase.auth.getSession();
  if (error) throw new Error(`Nie udało się sprawdzić sesji: ${error.message}`);
  if (!data?.session?.access_token) throw new Error('Sesja wygasła. Zaloguj się ponownie.');

  return data.session;
}

async function readJob(jobId) {
  const { data, error } = await supabase
    .from('calculator_jobs')
    .select('id,status,result,error_message,worker_id,attempt_count,created_at,started_at,completed_at')
    .eq('id', jobId)
    .single();

  if (error) {
    throw new Error(normalizeError(error, `Nie udało się odczytać zadania ${jobId}.`));
  }

  return data;
}

async function watchJob(jobId, originalPayload = {}) {
  if (activeJobs.has(jobId)) return activeJobs.get(jobId);

  const promise = (async () => {
    const startedAt = Date.now();
    let previousStatus = '';

    while (Date.now() - startedAt < WATCH_TIMEOUT_MS) {
      const job = await readJob(jobId);

      if (job.status !== previousStatus) {
        previousStatus = job.status;
        emit({
          source: 'D24_SERVER_CALCULATOR',
          transport: 'server',
          type: 'JOB_STATUS',
          payload: {
            ...originalPayload,
            serverJobId: jobId,
            status: job.status,
            workerId: job.worker_id,
            attemptCount: job.attempt_count,
          },
        });
      }

      if (job.status === 'completed') {
        if (!job.result || typeof job.result !== 'object') {
          throw new Error('Serwer oznaczył zadanie jako zakończone, ale nie zwrócił wyniku.');
        }

        emit({
          source: 'D24_SERVER_CALCULATOR',
          transport: 'server',
          type: 'CALCULATOR_RESULT',
          payload: {
            ...originalPayload,
            ...job.result,
            serverJobId: jobId,
          },
        });
        return job.result;
      }

      if (job.status === 'error' || job.status === 'cancelled') {
        throw new Error(job.error_message || `Zadanie zakończyło się statusem ${job.status}.`);
      }

      await sleep(POLL_INTERVAL_MS);
    }

    throw new Error('Serwer nie zakończył obliczenia w ciągu 15 minut. Zadanie może nadal działać w tle.');
  })()
    .catch(error => {
      emit({
        source: 'D24_SERVER_CALCULATOR',
        transport: 'server',
        type: 'JOB_ERROR',
        payload: { ...originalPayload, serverJobId: jobId },
        error: normalizeError(error, 'Nieznany błąd serwera kalkulatora.'),
      });
      throw error;
    })
    .finally(() => {
      activeJobs.delete(jobId);
    });

  activeJobs.set(jobId, promise);
  return promise;
}

export function onServerCalculatorMessage(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export async function startServerCalculatorJob(payload, { organizationId } = {}) {
  await requireSession();

  if (!organizationId) throw new Error('Brak organization_id użytkownika.');
  if (!SERVER_CALCULATORS.has(payload?.calculatorKey)) {
    throw new Error(`Kalkulator ${payload?.calculatorKey || 'bez klucza'} nie jest obsługiwany przez serwer.`);
  }
  if (!Array.isArray(payload?.points) || payload.points.length < 2) {
    throw new Error('Do obliczenia potrzebne są co najmniej dwa punkty.');
  }

  const row = {
    organization_id: organizationId,
    calculator_key: payload.calculatorKey,
    route_id: payload.routeId || null,
    route_version_id: payload.routeVersionId || null,
    section_id: payload.sectionId || null,
    section_order: Number.isFinite(Number(payload.sectionOrder)) ? Number(payload.sectionOrder) : null,
    payload,
    status: 'queued',
    job_source: 'manual',
  };

  const { data, error } = await supabase
    .from('calculator_jobs')
    .insert(row)
    .select('id,status,created_at')
    .single();

  if (error) {
    throw new Error(normalizeError(error, 'Nie udało się dodać zadania kalkulatora do kolejki.'));
  }

  emit({
    source: 'D24_SERVER_CALCULATOR',
    transport: 'server',
    type: 'JOB_ACCEPTED',
    payload: {
      ...payload,
      serverJobId: data.id,
      status: data.status,
      createdAt: data.created_at,
    },
  });

  watchJob(data.id, payload).catch(() => {});
  return data.id;
}

export async function resumeServerCalculatorJobs(organizationId) {
  if (!organizationId || !supabase) return [];
  await requireSession();

  const since = new Date(Date.now() - 30 * 60 * 1000).toISOString();
  const { data, error } = await supabase
    .from('calculator_jobs')
    .select('id,payload,status,created_at')
    .eq('organization_id', organizationId)
    .in('calculator_key', [...SERVER_CALCULATORS])
    .in('status', ['queued', 'processing'])
    .gte('created_at', since)
    .order('created_at', { ascending: true })
    .limit(10);

  if (error) {
    throw new Error(normalizeError(error, 'Nie udało się wznowić obserwowania zadań kalkulatorów.'));
  }

  for (const job of data || []) {
    emit({
      source: 'D24_SERVER_CALCULATOR',
      transport: 'server',
      type: 'JOB_ACCEPTED',
      payload: {
        ...(job.payload || {}),
        serverJobId: job.id,
        status: job.status,
        resumed: true,
      },
    });
    watchJob(job.id, job.payload || {}).catch(() => {});
  }

  return (data || []).map(job => job.id);
}
